import enterprise from "#api/enterprise-client";
import { showErrorNotification } from "#helpers/notifications";

import {
	Filters,
	GetTablesReportProps,
	TableProductReport,
	TablesReportTotals,
} from "#resources/api/enterprise-generated";
import { fetchModel } from "#resources/helpers/fetch-model";
import { RootStore } from ".";
import { INTERVAL_TIME } from "./live";

type LiveTableReportTotalsParams = {
	scope: GetTablesReportProps;
	filters: Filters;
};

type TableProductReportParams = {
	eventId?: string;
	placeId?: string;
	since?: Date;
	until?: Date;
};

export class TableStore {
	public rootStore: RootStore;
	private intervalId: null | number = null;
	private listenedEventId: null | string = null;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	public clean() {
		this.getLiveTableReportTotals.reset();
		this.getTableProductReport.reset();
	}

	private async update() {
		if (this.listenedEventId) {
			const eventId = this.listenedEventId;
			await this.getLiveTableReportTotals.fetch({
				scope: { eventId, place: null },
				filters: { status: null, tableNumbers: null, employeeIds: null },
			});
		} else if (this.intervalId !== null) {
			this.stopListening();
		} else {
			throw new Error(
				"Invalid TableStore.update state: listenedEventId is null, but intervalId is not",
			);
		}
	}

	public startListening(eventId: string) {
		this.stopListening();

		this.listenedEventId = eventId;
		this.intervalId = window.setInterval(() => this.update(), INTERVAL_TIME);

		this.update();
	}

	public stopListening() {
		if (this.intervalId !== null) {
			window.clearInterval(this.intervalId);
			this.intervalId = null;
		}

		this.listenedEventId = null;
		this.clean();
	}

	public getLiveTableReportTotals = new fetchModel<
		LiveTableReportTotalsParams,
		TablesReportTotals
	>({
		fnPromise: args => enterprise.getTableReportTotals(args),
		onError: err => showErrorNotification(err.message),
	});

	public getTableProductReport = new fetchModel<
		TableProductReportParams,
		TableProductReport[]
	>({
		fnPromise: args => enterprise.getTableProductReport(args),
		onError: err => showErrorNotification(err.message),
	});
}
